import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { isApiError } from '@/shared/api/errors'
import { log } from '@/shared/lib/log'
import { useT } from '@/shared/i18n'

/**
 * CONTRATO DEL REORDENAMIENTO. **El rango lo genera el servidor.**
 *
 * El cliente manda VECINOS y el server contesta el recurso ya con su
 * `priority_rank`:
 *
 *     POST /api/v1/projects/{id}/move
 *     POST /api/v1/tasks/{id}/move
 *     { "stage_id": "uuid|null", "before_id": "uuid|null", "after_id": "uuid|null" }
 *
 * `before_id: null` = primer puesto. `after_id: null` = último puesto.
 *
 * **Por qué el cliente NO genera el rango**, aunque hacerlo sea tentador y
 * aunque exista `fractional-indexing` para eso:
 *
 * 1. **Las carreras solo se resuelven de un lado.** Dos personas arrastrando a
 *    la vez calculan el mismo hueco entre los mismos vecinos y generan la misma
 *    clave. El server serializa con `pg_advisory_xact_lock` antes de calcular;
 *    el cliente no tiene con qué.
 * 2. **Los alfabetos no eran el mismo.** El backend usa base-36 en minúsculas
 *    sobre `varchar COLLATE "C"`; `fractional-indexing` emite prefijos en
 *    mayúscula (`Zz`) para insertar antes del primero. Mezclar las dos familias
 *    en una lista la ordena mal, y el bug aparece recién cuando alguien arrastra
 *    algo al puesto 1.
 * 3. **Dos generadores es dos veces la misma regla.** La que no se ejecuta en
 *    producción es la que se desincroniza sin que nadie se entere.
 *
 * Lo que queda del lado del cliente es `compareRanks` —- comparar por bytes, que
 * es lo que hace `COLLATE "C"`—- y el reordenamiento optimista del arreglo.
 *
 * ---
 *
 * Drag & drop sin salto visual (plan 3.4). El salto tiene tres causas y acá
 * están los tres antídotos:
 *
 * 1. **La posición optimista no coincide con la del server** → el update
 *    optimista NO inventa ninguna clave: saca el ítem de su índice y lo inserta
 *    en el nuevo. Cuando llega la respuesta, `reconcile` escribe el
 *    `priority_rank` autoritativo en su lugar, sin mover nada: el orden que se
 *    ve ya es el que el server acaba de persistir.
 * 2. **Un refetch en vuelo pisa la cache** → `cancelQueries` en `onMutate`, y
 *    `refetchOnWindowFocus: false` en las pantallas de orden.
 * 3. **Se invalida tras cada drop con otros drops en vuelo** → se invalida solo
 *    cuando soy la última mutación pendiente.
 *
 * Y el caso que parece un detalle y no lo es: si el backend contesta
 * `409 position_conflict` **no** se hace rollback. Rollback y refetch juntos
 * producen un doble salto — la fila vuelve a donde estaba y después salta a
 * donde la dejó la otra persona. Se refetchea y se avisa, nada más.
 */
export const REORDER_KEY = ['reorder'] as const

type CacheKey = readonly unknown[]
type Snapshot = [readonly unknown[], unknown][]

export interface ReorderMutationParams<TVars, TData> {
  mutationFn: (variables: TVars) => Promise<TData>
  /** Caches que toca este reorden: se cancelan, se parchean y se invalidan. */
  queryKeys: readonly CacheKey[]
  /** El cambio optimista. Corre después de `cancelQueries`. */
  apply: (client: QueryClient, variables: TVars) => void
  /**
   * El recurso que devolvió el server, con su rank autoritativo. Reemplaza al
   * optimista EN SU LUGAR: si esto reordenara, sería el salto que evitamos.
   */
  reconcile?: (client: QueryClient, data: TData, variables: TVars) => void
  /** Caches que solo hay que invalidar al final (contadores del portafolio). */
  invalidateOnSettled?: readonly CacheKey[]
  /**
   * Qué se dice cuando el guardado falla o cuando alguien reordenó primero.
   *
   * Los pasa el llamador **ya traducidos** con `t(...)`. Tenían un default en
   * español acá adentro, que es un lugar donde el idioma no se puede resolver:
   * este hook no sabe quién está mirando y un default escrito se muestra igual
   * aunque el llamador nunca lo pise. Ahora el default también sale del
   * catálogo, en el idioma de la sesión.
   */
  errorText?: string
  conflictText?: string
  /** Qué se está reordenando. Va al log para poder distinguirlos. */
  entity?: string
}

function snapshot(client: QueryClient, keys: readonly CacheKey[]): Snapshot {
  return keys.flatMap((key) => client.getQueriesData({ queryKey: key }))
}

function restore(client: QueryClient, entries: Snapshot): void {
  for (const [key, data] of entries) client.setQueryData(key, data)
}

export function useReorderMutation<TVars, TData>({
  mutationFn,
  queryKeys,
  apply,
  reconcile,
  invalidateOnSettled = [],
  errorText,
  conflictText,
  entity = 'item',
}: ReorderMutationParams<TVars, TData>) {
  const t = useT()
  const client = useQueryClient()
  const alFallar = errorText ?? t('dnd.reorderFailed')
  const alChocar = conflictText ?? t('dnd.reorderConflict')

  return useMutation({
    // La MISMA key para todos los reordenes de la app: el indicador de
    // "Guardando…" del header y el conteo de mutaciones pendientes dependen de
    // que sea una sola.
    mutationKey: REORDER_KEY,
    mutationFn,

    onMutate: async (variables: TVars) => {
      await Promise.all(queryKeys.map((key) => client.cancelQueries({ queryKey: key })))
      const previous = snapshot(client, queryKeys)
      apply(client, variables)
      return { previous }
    },

    onSuccess: (data, variables) => reconcile?.(client, data, variables),

    onError: (error, variables, context) => {
      if (isApiError(error) && error.status === 409) {
        // Sin rollback: la lista ya está "mal" de todas formas, y volver atrás
        // para después saltar al orden del server es el doble salto que
        // queremos evitar. Traemos la verdad y avisamos.
        log.warn('dnd_conflict', {
          entity,
          request_id: error.requestId,
          code: error.code,
          variables,
        })
        for (const key of queryKeys) void client.refetchQueries({ queryKey: key })
        toast.info(alChocar)
        return
      }

      // El rollback es exactamente el momento en que el usuario ve una fila
      // volver sola a su lugar. Si no queda registrado con su `request_id`, eso
      // llega como "se movió y se desmovió" y no hay nada que mirar.
      log.error('dnd_rollback', {
        entity,
        status: isApiError(error) ? error.status : 0,
        code: isApiError(error) ? error.code : 'unknown_error',
        request_id: isApiError(error) ? error.requestId : null,
        restored: Boolean(context?.previous),
        variables,
      })
      if (context?.previous) restore(client, context.previous)
      toast.error(alFallar)
    },

    onSettled: () => {
      // Invalidar solo cuando soy la última pendiente. Con tres drops rápidos,
      // invalidar en cada uno hace que la respuesta del primero repinte el orden
      // que ya cambiaron el segundo y el tercero.
      if (client.isMutating({ mutationKey: REORDER_KEY }) !== 1) return

      for (const key of [...queryKeys, ...invalidateOnSettled]) {
        void client.invalidateQueries({ queryKey: key })
      }
    },
  })
}
